import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import { APPS } from "../utils/appRegistry";

/* Every app, alphabetically, straight off the registry. This was four of them
   typed out by hand, which was fine at four and is not at sixteen: a list kept
   in two places drifts, and the copy that drifts is the one nobody looks at. */
const APP_ITEMS = Object.entries(APPS)
  .map(([to, app]) => ({ to, label: app.name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const LAUNCHER_ITEMS = [
  { to: "/posts", label: "All Posts" },
  { to: "/posts?category=blog", label: "Blog" },
  { to: "/photos", label: "Photos" },
  { to: "/posts?category=video", label: "Video" },
  { separator: true },
  { submenu: "apps", label: "Apps", items: APP_ITEMS },
  { separator: true },
  { to: "/about", label: "About" },
];

const SOCIAL_LINKS = [
  { href: "https://github.com/phillip-morrow", src: "/icons/github.png", label: "GitHub" },
  {
    href: "https://www.linkedin.com/in/phillip-m-2552ba5b/",
    src: "/icons/linkedin.png",
    label: "LinkedIn",
  },
  {
    href: "https://www.codewars.com/users/pmorrowbot",
    src: "/icons/codewars.svg",
    label: "Codewars",
  },
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const formatClock = (date) =>
  date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const formatFullDate = (date) =>
  date.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/**
 * The current month as rows of seven, Sunday first. Blank cells pad both ends
 * so every row is a full week and the grid stays rectangular.
 */
const monthWeeks = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const lead = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();

  const cells = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) => i + 1),
  ];
  while (cells.length % 7) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const Taskbar = ({ windows = [], focused, onWindowButton, theme, toggleTheme }) => {
  // One popover at a time, so opening the calendar closes the launcher.
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [clock, setClock] = useState(() => formatClock(new Date()));
  const launcherRef = useRef(null);
  const clockRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setClock(formatClock(new Date())), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!openMenu) return undefined;

    const container = openMenu === "launcher" ? launcherRef : clockRef;
    const onPointerDown = (e) => {
      if (!container.current?.contains(e.target)) closeAll();
    };
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      // Back out one level at a time, the way a real menu does.
      if (openSubmenu) setOpenSubmenu(null);
      else setOpenMenu(null);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu, openSubmenu]);

  const closeAll = () => {
    setOpenSubmenu(null);
    setOpenMenu(null);
  };

  const toggleMenu = (name) => {
    setOpenSubmenu(null);
    setOpenMenu((v) => (v === name ? null : name));
  };

  // Read fresh on each render rather than held in state. Renders only happen
  // when the clock string changes, so an open calendar rolls over at midnight
  // and costs nothing in between.
  const now = new Date();
  const fullDate = formatFullDate(now);

  return (
    <div className="taskbar">
      <div className="taskbar-launcher" ref={launcherRef}>
        <button
          type="button"
          className={`launcher-btn ${openMenu === "launcher" ? "is-open" : ""}`}
          onClick={() => toggleMenu("launcher")}
          aria-expanded={openMenu === "launcher"}
          aria-haspopup="menu"
        >
          {/* Decorative: the button already says where it goes. */}
          <img className="launcher-mark" src="/favicon.ico" alt="" />
          phillipmorrow.com
        </button>

        {openMenu === "launcher" && (
          <div className="launcher-menu">
            <div role="menu">
              {LAUNCHER_ITEMS.map((item, i) => {
                if (item.separator) {
                  return (
                    <div
                      key={`sep-${i}`}
                      className="launcher-separator"
                      role="separator"
                    />
                  );
                }

                if (item.submenu) {
                  const isOpen = openSubmenu === item.submenu;
                  return (
                    <div
                      key={item.submenu}
                      className="launcher-submenu"
                      onMouseEnter={() => setOpenSubmenu(item.submenu)}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        className={`launcher-item launcher-parent ${
                          isOpen ? "is-open" : ""
                        }`}
                        onClick={() =>
                          setOpenSubmenu((v) => (v === item.submenu ? null : item.submenu))
                        }
                      >
                        {item.label}
                        <span className="launcher-arrow" aria-hidden="true">
                          ▸
                        </span>
                      </button>

                      {isOpen && (
                        <div className="launcher-flyout" role="menu">
                          {item.items.map((app) => (
                            <Link
                              key={app.to}
                              to={app.to}
                              role="menuitem"
                              className="launcher-item"
                              onClick={closeAll}
                            >
                              {app.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    className="launcher-item"
                    /* Hovering a plain item closes any flyout, so the two are
                       never open against each other. */
                    onMouseEnter={() => setOpenSubmenu(null)}
                    onClick={closeAll}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="launcher-social">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                >
                  <img src={s.src} alt={s.label} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* One button per open window, in the order they were opened, which is
          the order a taskbar has always used. A rolled-up window reads as
          inactive; the focused one reads as pressed in. */}
      <div className="taskbar-windows">
        {windows.map((win) => (
          <button
            key={win.id}
            type="button"
            className={`taskbar-window-btn ${
              win.shaded ? "" : "is-active"
            } ${focused === win.id && !win.shaded ? "is-focused" : ""}`}
            onClick={() => onWindowButton(win.id)}
            aria-pressed={focused === win.id && !win.shaded}
            title={win.title}
          >
            {win.title}
          </button>
        ))}
      </div>

      <div className="taskbar-tray">
        <button
          type="button"
          className="tray-btn"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
        >
          {theme === "dark" ? "☀" : "☾"}
        </button>

        <div className="taskbar-clock" ref={clockRef}>
          {/* The button carries the full date as its label, so the flyout is
              extra rather than the only way to read it. Nothing inside it is
              focusable, which is why it stays a plain region: promising a
              dialog would promise focus handling that isn't here. */}
          <button
            type="button"
            className={`clock-btn ${openMenu === "calendar" ? "is-open" : ""}`}
            onClick={() => toggleMenu("calendar")}
            aria-expanded={openMenu === "calendar"}
            aria-label={fullDate}
            title={fullDate}
          >
            {clock}
          </button>

          {openMenu === "calendar" && (
            <div className="calendar-flyout">
              <table className="calendar">
                <caption>
                  {now.toLocaleDateString([], {
                    month: "long",
                    year: "numeric",
                  })}
                </caption>
                <thead>
                  <tr>
                    {WEEKDAYS.map((day) => (
                      <th key={day} scope="col">
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthWeeks(now).map((week, w) => (
                    <tr key={`week-${w}`}>
                      {week.map((day, d) => (
                        <td
                          key={day ?? `blank-${d}`}
                          className={day === now.getDate() ? "is-today" : ""}
                          aria-current={day === now.getDate() ? "date" : undefined}
                        >
                          {day}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="calendar-today">{fullDate}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Taskbar;
